import { Component, inject, signal, computed, effect, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AccessService } from '../../../services/access.service';
import { BusinessService } from '../../../services/business.service';
import { BranchService } from '../../../services/branch.service';
import { Branch } from '../../../models/branch.model';
import { SelectComponent } from '../select/select.component';
import { SelectOption } from '../select/select.model';

/**
 * Global business / branch context selector shown in the app header.
 *
 * SuperAdmin sees all businesses; non-admin users see only their
 * ACTIVE business memberships. Branch list updates when the selected
 * business changes.
 */
@Component({
  selector: 'app-context-selector',
  standalone: true,
  imports: [CommonModule, FormsModule, SelectComponent],
  templateUrl: './context-selector.component.html',
})
export class ContextSelectorComponent implements OnInit {
  accessService = inject(AccessService);
  businessService = inject(BusinessService);
  private branchService = inject(BranchService);

  /**
   * Businesses to show in the dropdown: all for SuperAdmin, else every business
   * the user has standing in.
   *
   * getScopedBusinessIds, not getAccessibleBusinessIds: the latter reads only the
   * *business* membership mirror, so a branch-only OWNER or MANAGER matched
   * nothing, the dropdown came up empty, no branch could be selected, and the
   * whole app was unusable for them.
   */
  selectableBusinesses = computed(() => {
    const all = this.businessService.businesses();
    if (this.accessService.isSuperAdmin()) return all;
    const ids = this.accessService.getScopedBusinessIds();
    return all.filter((b) => b.id && ids.includes(b.id));
  });

  branches = signal<Branch[]>([]);

  businessOptions = computed<SelectOption[]>(() =>
    this.selectableBusinesses()
      .filter((b) => b.id)
      .map((b) => ({ value: b.id!, label: b.name })),
  );

  branchOptions = computed<SelectOption[]>(() => [
    { value: '', label: 'All Branches' },
    ...this.branches()
      .filter((b) => b.id)
      .map((b) => ({ value: b.id!, label: b.name })),
  ]);

  constructor() {
    effect(() => {
      const bizId = this.accessService.selectedBusinessId();
      if (bizId) {
        this.loadBranches(bizId);
      } else {
        this.branches.set([]);
      }
    });

    // Load businesses when access is ready. SuperAdmin: full list. Others: fetch
    // by ID from the membership mirrors, so they never depend on collection-query
    // rules (which Firestore evaluates per returned document and would deny
    // wholesale).
    effect(() => {
      if (!this.accessService.loaded()) return;
      if (this.accessService.isSuperAdmin()) {
        this.businessService.getBusinesses();
        return;
      }
      const ids = this.accessService.getScopedBusinessIds();
      if (ids.length > 0) {
        this.businessService.getBusinessesByIds(ids);
      } else {
        this.businessService.businesses.set([]);
      }
    });

    // When there's exactly one selectable business and none selected yet,
    // default to it (so the dropdown shows it as selected once options exist).
    effect(() => {
      const list = this.selectableBusinesses();
      if (list.length === 1 && !this.accessService.selectedBusinessId()) {
        const id = list[0].id;
        if (id) this.accessService.selectBusiness(id);
      }
    });
  }

  private async loadBranches(businessId: string): Promise<void> {
    // Scoped inside the service — see BranchService.getBranches.
    this.branches.set(await this.branchService.getBranches(businessId));
  }

  async ngOnInit(): Promise<void> {
    // Initial load is done in constructor effect when accessService.loaded() and
    // (for non-SuperAdmin) membership mirror are ready.
  }

  async onBusinessChange(value: string): Promise<void> {
    await this.accessService.selectBusiness(value || null);
    if (value) {
      await this.loadBranches(value);
    }
  }

  async onBranchChange(value: string): Promise<void> {
    await this.accessService.selectBranch(value || null);
  }
}
